/**
 * KalenderSidebar — schlanke Seitenleiste (Desktop, Google-Calendar-Stil)
 * Mini-Monat zum schnellen Navigieren + Betriebs-Filter.
 * Mobil ausgeblendet (hidden lg:flex), damit die bestehende Mobile-Ansicht unverändert bleibt.
 */
import { useMemo } from 'react';
import { Plus } from 'lucide-react';
import { BETRIEB_LIST, BETRIEBE, type BetriebId } from '@/lib/betriebe';

const WD = ['M', 'D', 'M', 'D', 'F', 'S', 'S'];
const MONTHS = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export default function KalenderSidebar({ currentDate, onPickDate, betriebFilter, onSetBetrieb, onNewEvent }: {
  currentDate: Date;
  onPickDate: (d: Date) => void;
  betriebFilter: string;
  onSetBetrieb: (id: string) => void;
  onNewEvent: () => void;
}) {
  const today = startOfDay(new Date());

  const cells = useMemo(() => {
    const first = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const offset = (first.getDay() + 6) % 7; // Montag-basiert
    const start = new Date(first);
    start.setDate(first.getDate() - offset);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [currentDate]);

  return (
    <aside
      className="hidden lg:flex flex-col w-[200px] flex-shrink-0 gap-4 bg-white rounded-xl border border-gray-100 p-3 overflow-y-auto"
      style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
    >
      <button
        onClick={onNewEvent}
        className="inline-flex items-center gap-2 self-start rounded-full border border-gray-200 px-3.5 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
      >
        <Plus className="w-4 h-4" /> Eintragen
      </button>

      <div>
        <div className="text-xs font-medium text-gray-600 mb-1.5 px-1">
          {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
        </div>
        <div className="grid grid-cols-7 gap-0.5 text-[10px] text-gray-400 text-center mb-1">
          {WD.map((w, i) => <span key={i}>{w}</span>)}
        </div>
        <div className="grid grid-cols-7 gap-0.5 text-[11px] text-center">
          {cells.map((d, i) => {
            const isCur = d.getMonth() === currentDate.getMonth();
            const isToday = sameDay(d, today);
            const isSel = sameDay(d, currentDate);
            return (
              <button
                key={i}
                onClick={() => onPickDate(d)}
                className={`h-6 w-6 mx-auto rounded-full flex items-center justify-center transition-colors ${
                  isToday
                    ? 'bg-[#1a73e8] text-white'
                    : isSel
                      ? 'bg-gray-100 text-gray-900'
                      : isCur
                        ? 'text-gray-700 hover:bg-gray-100'
                        : 'text-gray-300 hover:bg-gray-50'
                }`}
              >
                {d.getDate()}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wider px-1 mb-1">Betriebe</div>
        <button
          onClick={() => onSetBetrieb('alle')}
          className={`flex items-center gap-2 px-1.5 py-1 rounded-md text-xs text-left transition-colors ${
            betriebFilter === 'alle' ? 'font-medium text-gray-900 bg-gray-50' : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          <span className="w-2.5 h-2.5 rounded-sm bg-gray-300 flex-shrink-0" /> Alle Betriebe
        </button>
        {BETRIEB_LIST.filter(b => b.hasData).map(b => (
          <button
            key={b.id}
            onClick={() => onSetBetrieb(b.id)}
            className={`flex items-center gap-2 px-1.5 py-1 rounded-md text-xs text-left transition-colors ${
              betriebFilter === b.id ? 'font-medium text-gray-900 bg-gray-50' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <span
              className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
              style={{ backgroundColor: BETRIEBE[b.id as BetriebId]?.color?.accent || '#9aa0a6' }}
            />
            <span className="truncate">{b.name}</span>
          </button>
        ))}
      </div>
    </aside>
  );
}
